/**
 * Nested milk-share helpers — the per-SKU layer under the Milk
 * (BEVERAGE) component kind (by-component percentage ordering).
 *
 * The top-level BEVERAGE % decides how many milks ship per meals
 * ordered; these helpers decide how that MILK TOTAL splits across the
 * school's linked milk SKUs (`LocationMilkShareRate` rows, or an even
 * split when the school configured none). Shares that don't sum to
 * 100% are never hard-blocked — apportionment normalizes
 * proportionally, and the UI shows an inline warning.
 *
 * Quantities distribute by LARGEST-REMAINDER apportionment so the
 * per-SKU counts always sum exactly to the milk total (per-SKU
 * ceil/round would drift). Client mirror of
 * `packages/schema/src/managers/milk-share-resolver.ts`.
 *
 * Pure (no React) — unit-tested in `client/test/lib/school-orders/`.
 */

import { apportionQuantity } from '@ordo/constants/milk-derivation'
import {
  CANONICAL_MILK_OPTION_CODES,
  type CanonicalMilkOptionCode,
  normalizeMilkOptionCode,
} from '@ordo/constants/milk-options'

/** Canonical form of a SKU's catalog stamp (null/undefined/unknown → null). */
function stampedMilkOptionCode(stamp: string | null | undefined): CanonicalMilkOptionCode | null {
  return stamp == null ? null : normalizeMilkOptionCode(stamp)
}

/** One milk SKU (Component) in the school's % split (group-pref link or lineup type). */
export interface MilkSkuWire {
  id: string
  name: string
  /** Authoritative catalog milk-type stamp; beats name inference when present. */
  milkOptionCode?: string | null
}

/**
 * One share row off `/api/location-settings/:id/milk-shares`. Rows are
 * scoped by the `(groupDimension, groupValue)` enrollment-group tuple:
 * AGE_GROUP values carry the `LocationGradeRange` column id, every other
 * dimension carries the vocabulary value label; null/null = the
 * location-wide fallback row. Rows may additionally be scoped to one
 * mealtime (null/absent = the legacy all-mealtimes fallback).
 */
export interface MilkShareRateWire {
  componentId: string
  shareBps: number
  groupDimension: 'AGE_GROUP' | 'GRADE' | 'TEACHER' | 'ROOM' | 'HOMEROOM' | 'LUNCH_PERIOD' | null
  groupValue: string | null
  mealtime?: string | null
}

/**
 * A rate/share row's order-column key: the AGE_GROUP column id, the
 * `"<dimension>::<value>"` tuple key, or null for a location-wide row —
 * matching `OrderColumn.columnId` for both column kinds.
 */
export function rateRowColumnKey(row: { groupDimension: string | null; groupValue: string | null }): string | null {
  if (!row.groupDimension || row.groupValue === null) {
    return null
  }
  return row.groupDimension === 'AGE_GROUP' ? row.groupValue : `${row.groupDimension}::${row.groupValue}`
}

/**
 * A rate/share row as the column-key passes see it.
 */
export interface ColumnKeyedRow {
  groupDimension: string | null
  groupValue: string | null
  mealtime?: string | null
}

/**
 * BAND COLUMN FOLD — the campus column key one axis of a band column's
 * lookup falls back to, or null when none of them has a row on that axis.
 *
 * The order sheet identifies an AGE_GROUP column by the canonical BAND
 * `GradeRange` id; every per-column rate/share row a school ever authored is
 * stored under the campus `LocationGradeRange` id the SETTINGS grid uses. So a
 * band column also accepts rows keyed to the campus columns folding into it —
 * ranked BELOW its own exact-band rows and ABOVE the location-wide fallback.
 * `column.coveredLocationGradeRangeIds` carries the candidates, ordered by
 * `bandFoldColumnIds` (@ordo/constants) — most preferred first, the SAME
 * ordering the server's `band-column-fold.ts` uses, so the sheet's preview and
 * the numbers the server ships can never disagree.
 *
 * EXACTLY ONE campus column supplies the fallback for an axis: the most
 * preferred one with any row there. Two campus columns folding into one band
 * hold two independent opinions and are never merged or summed — combining two
 * milk splits would double the percentages.
 */
export function foldedColumnKeyFor<Row extends ColumnKeyedRow>(
  rows: readonly Row[],
  foldedColumnIds: readonly string[] | undefined,
  onAxis: (row: Row) => boolean,
): string | null {
  return (
    (foldedColumnIds ?? []).find((candidate) =>
      rows.some((row) => onAxis(row) && rateRowColumnKey(row) === candidate),
    ) ?? null
  )
}

/**
 * Largest-remainder apportionment — the shared implementation in
 * `@ordo/constants/milk-derivation` (the same function the server-side
 * field-trip milk placer runs, so the sheet and the placer can never split a
 * total differently). Re-exported so existing callers keep their import.
 */
export { apportionQuantity }

/**
 * The even-split default as integer percents summing exactly to 100
 * (3 SKUs → [34, 33, 33]). Index-aligned with the input order.
 */
export function evenSplitPct(count: number): number[] {
  if (count <= 0) {
    return []
  }
  const keys = Array.from({ length: count }, (_, i) => String(i))
  const split = apportionQuantity(100, Object.fromEntries(keys.map((k) => [k, 1])))
  return keys.map((k) => split[k])
}

/**
 * The effective share % per milk SKU for one (order column, mealtime):
 * overlay passes, least → most specific — (legacy, wide) → (legacy, FOLD) →
 * (legacy, column) → (mealtime, wide) → (mealtime, FOLD) → (mealtime, column)
 * — later passes overwrite per SKU. A school with NO configured row for any
 * linked SKU gets the even-split default; unconfigured SKUs in a
 * partially-configured set get 0. Omitting the mealtime keeps the
 * legacy two-tier behavior (mealtime-scoped rows invisible). Mirrors
 * `MilkShareResolver.resolveShares` (bps → integer percent).
 *
 * `foldedColumnIds` are the campus grade-range column keys a menu-derived BAND
 * column falls back to (`OrderColumn.coveredLocationGradeRangeIds`) — see
 * `foldedColumnKeyFor`. Omit them and resolution is exactly what it was.
 */
export function milkSharePctForColumn(
  shares: readonly MilkShareRateWire[],
  skuIds: readonly string[],
  columnId: string,
  mealtime?: string | null,
  foldedColumnIds?: readonly string[],
): Record<string, number> {
  if (skuIds.length === 0) {
    return {}
  }
  const skuSet = new Set(skuIds)
  const effective: Record<string, number> = {}
  const isLegacy = (row: MilkShareRateWire) => (row.mealtime ?? null) === null
  const isMealtime = (row: MilkShareRateWire) => mealtime != null && row.mealtime === mealtime
  const passes: Array<(row: MilkShareRateWire) => boolean> = []
  for (const onAxis of [isLegacy, isMealtime]) {
    passes.push((row) => onAxis(row) && rateRowColumnKey(row) === null)
    const foldKey = foldedColumnKeyFor(shares, foldedColumnIds, onAxis)
    if (foldKey !== null && foldKey !== columnId) {
      passes.push((row) => onAxis(row) && rateRowColumnKey(row) === foldKey)
    }
    passes.push((row) => onAxis(row) && rateRowColumnKey(row) === columnId)
  }
  for (const matches of passes) {
    for (const row of shares) {
      if (skuSet.has(row.componentId) && matches(row)) {
        effective[row.componentId] = row.shareBps
      }
    }
  }
  if (Object.keys(effective).length === 0) {
    const even = evenSplitPct(skuIds.length)
    return Object.fromEntries(skuIds.map((id, i) => [id, even[i]]))
  }
  return Object.fromEntries(skuIds.map((id) => [id, Math.round((effective[id] ?? 0) / 100)]))
}

/** Sum of a share map's percents — 100 means "no normalization needed". */
export function shareSumPct(sharePct: Record<string, number>): number {
  return Object.values(sharePct).reduce((sum, pct) => sum + (Number.isFinite(pct) ? pct : 0), 0)
}

// ---------------------------------------------------------------------------
// School milk profile ↔ linked-SKU reconciliation.
//
// The school-level milk profile (`OrganizationMenuProfile.milkOptionCodes` +
// `noMilkMode`) is the district's declaration of WHICH milk TYPES it serves —
// keyed by MENU_MILK_OPTION_CODE (e.g. '1_CHOCOLATE'). The milk-share editor's
// editable rows are keyed by the linked milk SKU (a Component id), and there is
// NO structured SKU→type mapping in the catalog: a Component carries only a
// coarse `orderDefaultTargetKey` (BEVERAGE_MILK/…FLAVORED/…UNFLAVORED) and a
// canonical NAME. So we infer a code from the SKU name best-effort — and treat
// an unrecognized name as "unmapped" (null) rather than guessing, so we never
// emit a false mismatch warning. This informs the editor (surfaces the profile
// as source-of-truth + flags gaps); it never re-keys the share rows.
// ---------------------------------------------------------------------------

/** Milk profile shape off `/api/location-settings/:id/milk-shares`. */
export interface MilkProfileWire {
  /** The district's chosen milk types (already filtered to valid codes server-side). */
  milkOptionCodes: CanonicalMilkOptionCode[]
  /** True = the school explicitly serves no milk. */
  noMilkMode: boolean
}

const ONE_PCT = /(^|[^0-9])1\s*%|low\s*fat/i
const ZERO_PCT = /(^|[^0-9])0\s*%|skim|fat[\s-]?free|non[\s-]?fat/i

/**
 * Best-effort inference of a canonical milk-type code from a milk SKU's name.
 * Returns null when the name doesn't confidently resolve to one of the
 * `CANONICAL_MILK_OPTION_CODES`. Used
 * only for display/reconciliation, never to key or persist a share row.
 */
export function inferMilkOptionCode(name: string): CanonicalMilkOptionCode | null {
  const n = name.toLowerCase()
  // Display only. An explicit unsupported serving must not be relabelled as 8 oz.
  const plant = /\b(soy|oat|almond|pea)\b/.exec(n)?.[1]
  if (plant) {
    const ounces = /(^|[^0-9])(\d+(?:\.\d+)?)\s*(?:fl\s*)?oz/.exec(n)?.[2]
    const size = ounces ? Number(ounces) : 8
    if (plant === 'soy' && size === 6) {
      return 'SOY_6OZ'
    }
    if (size !== 8) {
      return null
    }
    return ({ soy: 'SOY_8OZ', oat: 'OAT_8OZ', almond: 'ALMOND_8OZ', pea: 'PEA_8OZ' } as const)[
      plant as 'soy' | 'oat' | 'almond' | 'pea'
    ]
  }
  // Lactose-free and whole milk are served at TWO sizes (8 oz K-12 carton vs
  // 6 oz preschool/CACFP bulk), so the size in the name picks the code. Small
  // bulk sizes (4/6 oz) read as the bulk-service code; no size in the name
  // defaults to the 8 oz carton — the historical meaning of the retired
  // size-less codes.
  const isBulkSize = /(^|[^0-9])[46]\s*(?:fl\s*)?oz/.test(n)
  if (/lactaid|lactose[\s-]?free/.test(n)) {
    return isBulkSize ? '1_LACTOSE_FREE_6OZ' : '1_LACTOSE_FREE_8OZ'
  }
  if (/\bwhole\b/.test(n) || /3\.25/.test(n)) {
    return isBulkSize ? 'WHOLE_MILK_6OZ' : 'WHOLE_MILK_8OZ'
  }
  const isChocolate = /chocolate|\bchoc\b/.test(n)
  if (ONE_PCT.test(n)) {
    return isChocolate ? '1_CHOCOLATE' : '1_REGULAR'
  }
  if (ZERO_PCT.test(n)) {
    return isChocolate ? '0_CHOCOLATE' : '0_REGULAR'
  }
  return null
}

/** A linked milk SKU paired with its best-effort inferred type code. */
export interface MilkSkuWithCode extends MilkSkuWire {
  inferredCode: CanonicalMilkOptionCode | null
}

/** Reconciliation between the school milk profile and its linked milk SKUs. */
export interface MilkProfileReconciliation {
  /** Each linked SKU annotated with its inferred milk-type code (null = unmapped). */
  skus: MilkSkuWithCode[]
  /** Profile types the district serves that have NO matching linked SKU. */
  profileCodesWithoutSku: CanonicalMilkOptionCode[]
  /** Linked SKUs whose inferred type is not in the district's milk profile. */
  skusNotInProfile: MilkSkuWithCode[]
}

/**
 * Reconcile the school milk profile against its linked milk SKUs so the editor
 * can surface the profile as source-of-truth and flag gaps in EITHER direction:
 *   - a profile type the school serves but hasn't linked a SKU for, and
 *   - a linked SKU whose type the profile doesn't list.
 * Directional gap lists are only populated when the profile actually declares
 * milk types (an empty profile makes no claim, so nothing is "missing").
 * SKUs whose name doesn't map to a code (inferredCode === null) never count as
 * a mismatch — we don't guess.
 */
export function reconcileMilkProfile(
  milkSkus: readonly MilkSkuWire[],
  milkProfileCodes: readonly CanonicalMilkOptionCode[],
): MilkProfileReconciliation {
  // The catalog stamp is authoritative when present (lineup-derived SKUs
  // always carry it); name inference remains the unstamped-SKU fallback.
  const skus: MilkSkuWithCode[] = milkSkus.map((sku) => ({
    ...sku,
    inferredCode: stampedMilkOptionCode(sku.milkOptionCode) ?? inferMilkOptionCode(sku.name),
  }))
  const profileSet = new Set<CanonicalMilkOptionCode>(milkProfileCodes)
  const linkedCodes = new Set<CanonicalMilkOptionCode>(
    skus.map((s) => s.inferredCode).filter((c): c is CanonicalMilkOptionCode => c !== null),
  )
  const hasProfile = profileSet.size > 0
  const profileCodesWithoutSku = hasProfile
    ? CANONICAL_MILK_OPTION_CODES.filter((code) => profileSet.has(code) && !linkedCodes.has(code))
    : []
  const skusNotInProfile = hasProfile
    ? skus.filter((s) => s.inferredCode !== null && !profileSet.has(s.inferredCode))
    : []
  return { skus, profileCodesWithoutSku, skusNotInProfile }
}
